import { TimetableReportType } from '@bp/planung-graphql-types';
import { Card, EmptyState, Grid, GridColumn, GridRow, NoTasksIcon } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { useActiveTimetableVersionsQuery, useTimetableReportsQuery } from '../../types/planung-graphql-client-defs';
import { useTeachingBlocks } from '../Teachingblock/hooks/useTeachingBlocks';
import {
  TimetableReportTypesTable,
  TimetableReportTypesTableType,
} from '../Timetable/tables/TimetableReportTypesTable';
import { useTimetableBlocks } from '../TimetableBlock/hooks/useTimetableBlocks';
import { TimetablePrintConfigurationModal } from '../TimetablePrintConfiguration/modals/TimetablePrintConfigurationModal';
import { ActiveVersionsReport, ActiveVersionType } from './ActiveVersionsReport';
import { TimetableReportsTable, TimetableReportsTableType } from './TimetableReportsTable';
import { ReportTopic } from './types';

type TimetableReportsProps = {
  topic: ReportTopic;
};

export const TimetableReports = observer(({ topic }: TimetableReportsProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();
  const timetableVersionsContext = useMemorizedCacheTag('TIMETABLE_VERSIONS');

  const tableData: TimetableReportTypesTableType[] = useMemo(() => {
    switch (topic) {
      case 'classes':
        return [
          { type: TimetableReportType.SingleClass, name: t('pdf.singlePlans') },
          { type: TimetableReportType.AllClasses, name: t('pdf.overallPlans') },
          {
            type: TimetableReportType.TeachingBlocksClass,
            name: t('pdf.epochPlans'),
          },
          {
            type: TimetableReportType.TeachingBlocksAllClasses,
            name: t('pdf.epochOverallPlans'),
          },
          {
            type: TimetableReportType.TeachingBlocksAllClassesCombined,
            name: t('pdf.epochOverallPlansCombined'),
          },
          {
            type: TimetableReportType.AttendanceSchedule,
            name: t('pdf.attendanceSchedule'),
          },
        ];
      case 'rooms':
        return [
          {
            type: TimetableReportType.SingleRoom,
            name: t('pdf.singlePlans'),
          },
          { type: TimetableReportType.DoorSigns, name: t('pdf.doorSigns') },
          { type: TimetableReportType.AllRooms, name: t('pdf.overallPlans') },
          {
            type: TimetableReportType.TeachingBlocksRoom,
            name: `${t('pdf.epochPlans')}`,
          },
        ];
      case 'teachers':
        return [
          {
            type: TimetableReportType.SingleTeacher,
            name: t('pdf.singlePlans'),
          },
          {
            type: TimetableReportType.AllTeachers,
            name: t('pdf.overallPlans'),
          },
          {
            type: TimetableReportType.TeachingBlocksTeacher,
            name: t('pdf.epochPlans'),
          },
        ];
      case 'other':
        return [
          {
            type: TimetableReportType.SchoolTrips,
            name: t('pdf.classTrips'),
          },
        ];
      default:
        return [];
    }
  }, [topic, t]);

  const [headline, setHeadline] = useState<string>(tableData[0].name);
  const [subType, setSubType] = useState<TimetableReportType | null>(tableData[0].type);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);

  // Schuljahr-weite Reports: gelten fürs ganze Schuljahr (nicht pro Block) und werden – wie
  // Klassenfahrten – über die SchoolYear-UUID erzeugt/aufgelistet.
  const isSchoolYearReport =
    subType === TimetableReportType.SchoolTrips ||
    subType === TimetableReportType.TeachingBlocksAllClasses ||
    subType === TimetableReportType.TeachingBlocksAllClassesCombined;

  const { teachingBlocks } = useTeachingBlocks(currentSchoolYear?.uuid, { onlyWithActiveVersion: true });

  const { timetableBlocks } = useTimetableBlocks(currentSchoolYear?.uuid, { onlyWithActiveVersion: true });
  // Versionen und Stundenpläne ändern sich über Mutationen mit anderen Typnamen als diese Abfrage.
  const [{ data: activeVersionsData }] = useActiveTimetableVersionsQuery({
    variables: { schoolYearUuid: currentSchoolYear?.uuid ?? '' },
    pause: !currentSchoolYear?.uuid,
    context: timetableVersionsContext,
  });

  const activeVersions: ActiveVersionType[] = useMemo(
    () =>
      (activeVersionsData?.activeTimetableVersions ?? []).map((version) => ({
        uuid: version.uuid,
        version: version.version,
        timetableName: version.timetableName,
        classUuids: version.classUuids,
        roomUuids: version.roomUuids,
        teacherUuids: version.teacherUuids,
      })),
    [activeVersionsData],
  );

  const [{ data: timetableReportsQueryData }, refetchReports] = useTimetableReportsQuery({
    variables: isSchoolYearReport
      ? { schoolYearUuid: currentSchoolYear?.uuid }
      : {
          teachingBlockUuids: teachingBlocks?.map((tb) => tb.uuid) ?? [],
          timetableBlockUuids: timetableBlocks?.map((tb) => tb.uuid) ?? [],
        },
    pause: isSchoolYearReport
      ? !currentSchoolYear?.uuid
      : teachingBlocks?.length === 0 && timetableBlocks?.length === 0,
    // Berichte entstehen über die PDF-Routen, nicht über Mutationen: der Cache erfährt davon nichts.
    requestPolicy: 'cache-and-network',
  });

  const isSingle =
    subType === TimetableReportType.SingleClass ||
    subType === TimetableReportType.SingleRoom ||
    subType === TimetableReportType.SingleSubject ||
    subType === TimetableReportType.SingleTeacher;

  const isTeachingBlockReport =
    subType === TimetableReportType.TeachingBlocksClass ||
    subType === TimetableReportType.TeachingBlocksTeacher ||
    subType === TimetableReportType.TeachingBlocksRoom ||
    subType === TimetableReportType.TeachingBlocksSubject;

  const timetableReportsData: TimetableReportsTableType[] =
    timetableReportsQueryData?.timetableReports
      .filter((report) => report.type === subType)
      .map((report) => {
        return {
          uuid: report.uuid,
          name: report.title,
          contextUuid: report.contextUuid,
          contextName: report.contextName,
          type: isSingle ? (report.fileEntries.length > 1 ? t('pdf.singlePdf') : t('pdf.collectivePdf')) : '',
          timestamp: dayjs(report.jobFinished),
        };
      })
      .sort((a, b) => {
        return a.timestamp.isAfter(b.timestamp) ? -1 : 1;
      }) ?? [];

  function handleSelect(headline: string, subType: TimetableReportType | null) {
    setHeadline(headline);
    setSubType(subType);
  }

  return (
    <>
      <Grid>
        <GridRow mobileGap={'32px'}>
          <GridColumn width={6}>
            <Card header={{ headline: t('timetable.title', { count: 2 }) }} contentPadding={'none'}>
              <TimetableReportTypesTable
                data={tableData}
                onSelect={handleSelect}
                onConfigModal={() => !loading && setShowConfigModal(true)}
              />
            </Card>
          </GridColumn>
          <GridColumn width={6}>
            {!subType ? (
              <Card header={{ headline }} contentPadding='none'>
                <EmptyState title={t('common.commingSoon')} icon={<NoTasksIcon />} padding='l' />
              </Card>
            ) : isSchoolYearReport ? (
              <Card header={{ headline }} contentPadding='none'>
                <div key={currentSchoolYear?.uuid}>
                  <div className='px-6 mb-4'>{currentSchoolYear?.name}</div>
                  <TimetableReportsTable
                    contextUuid={currentSchoolYear?.uuid ?? ''}
                    tableData={timetableReportsData}
                    refetchTableData={refetchReports}
                    type='timetable'
                    subType={subType}
                    onLoad={setLoading}
                  />
                </div>
              </Card>
            ) : isTeachingBlockReport ? (
              <Card header={{ headline }} contentPadding='none'>
                {teachingBlocks?.map((teachingBlock) => {
                  return (
                    <div key={teachingBlock.uuid}>
                      <div className='px-6 mb-4'>{teachingBlock.name}</div>
                      <TimetableReportsTable
                        contextUuid={teachingBlock.uuid}
                        tableData={timetableReportsData}
                        refetchTableData={refetchReports}
                        type='teachingblock'
                        subType={subType}
                        onLoad={setLoading}
                      />
                    </div>
                  );
                })}
              </Card>
            ) : (
              <>
                {/* Der /pdf/display-Endpunkt rendert immer Einzelpläne – daher nur dort anbieten. */}
                {isSingle && (
                  <Card
                    header={{ headline: t('pdf.forActiveVersions', { type: headline }) }}
                    contentPadding='none'
                    wrapperStyle={{ marginBottom: 'var(--spacing-6)' }}
                  >
                    <ActiveVersionsReport topic={topic} versions={activeVersions} onLoad={setLoading} />
                  </Card>
                )}
                <Card header={{ headline: t('pdf.forTimetableBlocks', { type: headline }) }} contentPadding='none'>
                  {[...(timetableBlocks ?? [])]
                    .sort((a, b) => {
                      return dayjs(a.start).isAfter(dayjs(b.start)) ? -1 : 1;
                    })
                    .map((timetableBlock) => {
                      return (
                        <div key={timetableBlock.uuid}>
                          <div className='px-6 mb-4'>{timetableBlock.name}</div>
                          <TimetableReportsTable
                            tableData={timetableReportsData}
                            refetchTableData={refetchReports}
                            contextUuid={timetableBlock.uuid}
                            type='timetable'
                            subType={subType}
                            onLoad={setLoading}
                          />
                        </div>
                      );
                    })}
                </Card>
              </>
            )}
          </GridColumn>
        </GridRow>
      </Grid>

      {subType && (
        <TimetablePrintConfigurationModal
          isOpen={showConfigModal}
          subType={subType}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </>
  );
});
